/**
 * HoriPage — surface vocale dépouillée (UX-1 = A, US-VDPI-03, plan §13.1).
 *
 * Tout passe par la voix (machine à états §5 via `useDialogueSession`).
 * Plus de dropdown patient / transcript history / offcanvas / boutons d'action :
 * un recorder d'état central (cercle violet + halo + barres sonores, 4 états
 * visuels dérivés du dialogue), une ligne d'echo du dernier speak, un repère
 * discret patient courant. Un seul interrupteur pour armer la boucle wake
 * (nécessite un geste utilisateur pour le micro).
 *
 * Le visuel utilise le composant DS canonique `VoiceRecorder` (cercle violet +
 * halo + 9 barres, 4 états). Aucune logique vocale n'est portée ici : tout vient
 * de `useDialogueSession`. La logique « armé ou non » reste locale : on passe
 * `state={armed ? orbe : 'idle'}`.
 */

import { useEffect, useState } from 'react';
import { VoiceRecorder, VoiceStatusPill } from '@/shared/ds/patterns';
import { VOICE_LABEL, voiceState } from '@/shared/ds/patterns/VoiceRecorder/voiceStates';
import type { VoiceRecorderState } from '@/shared/ds/patterns/VoiceRecorder/VoiceRecorder';
import { usePersona } from '@/shared/contexts/PersonaContext';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { soignantTitre } from '@/shared/types/user';
import { useDpiPolling } from '@/features/admin/hooks/useDpiPolling';
import { JournalPanel } from '@/shared/components/dpi';
import { useDialogueSession } from '../hooks/useDialogueSession';
import type { DialogueContext, DialogueStateName } from '../services/dialogueMachine';
import { ContextPanel } from '../components/ContextPanel';
import '../voice-orbe.css';

interface HoriPageProps {
  /** US-VMPL-001 : appelé à chaque changement de phase ou de contexte du dialogue. */
  onDialogue?: (phase: DialogueStateName, ctx: DialogueContext) => void;
  /** US-VMPL-001/002 : mode /vocal2 — masque le journal, affiche l'entrée texte (pilotage SON, repli clavier). */
  compact?: boolean;
  /** VREC1 S13 : appelé quand l'état du cercle change (ombre de la fenêtre /vocal2). */
  onVoiceState?: (state: VoiceRecorderState) => void;
}

export function HoriPage({ onDialogue, onVoiceState, compact = false }: HoriPageProps = {}) {
  const [armed, setArmed] = useState(false);
  const [texte, setTexte] = useState('');
  /** /vocal2 : détail sous la pastille, replié par défaut ; ouvert, il reste ouvert d'un état à l'autre (VREC1 D7). */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { ttsStatus } = usePersona();
  const { user } = useAuth();
  const { data: dpi } = useDpiPolling();
  const { prompt, patientLabel, error, phase, ctx, capturing, awaitingCommand, busy, listening, entendu } =
    useDialogueSession(armed);

  useEffect(() => {
    onDialogue?.(phase, ctx);
  }, [phase, ctx, onDialogue]);

  // L'état du cercle suit les signaux physiques (B2) ; une seule règle de priorité, dans le DS (VREC1 §3.6).
  // `awaitingCommand` (Q-1) = micro wake en écoute de commande → même état que la capture VAD.
  const recorderState = voiceState({
    armed,
    error: Boolean(error),
    busy,
    capturing: capturing || awaitingCommand,
    confirming: phase === 'AWAIT_COMMIT',
    listening,
  });

  useEffect(() => {
    onVoiceState?.(recorderState);
  }, [recorderState, onVoiceState]);

  return (
    <div className={compact ? 'vorbe-page vorbe-page--compact' : 'vorbe-page'}>
      {/* /vocal2 : soignant connecté, en une ligne discrète (VREC1 S14) */}
      {compact && user && (
        <div className="vorbe-soignant" title={`${soignantTitre(user.profil)} : ${user.prenom} ${user.nom}`}>
          <strong>{soignantTitre(user.profil)}</strong> : {user.prenom} {user.nom}
        </div>
      )}
      {/* Repère discret patient courant */}
      {patientLabel && (
        <div className="vorbe-patient">
          <i className="bi bi-person-vcard" />
          {patientLabel}
        </div>
      )}

      {/* Recorder d'état central — interrupteur de la boucle wake (geste micro requis) */}
      <VoiceRecorder
        state={recorderState}
        size={compact ? 'sm' : 'lg'}
        onToggle={() => setArmed((v) => !v)}
        ariaLabel={armed ? 'Désactiver la saisie vocale' : 'Activer la saisie vocale'}
      />

      {/* /vocal2 : pastille d'état sous le petit cercle ; son chevron déplie le détail (VREC1 S11) */}
      {compact && (
        <VoiceStatusPill
          state={recorderState}
          expanded={detailsOpen}
          detailsId="vocal2-details"
          onToggleDetails={() => setDetailsOpen((o) => !o)}
        />
      )}

      {/* Détail : repliable en /vocal2 (reste dans le DOM, formulaire SON compris) ; transparent en /vocal (display: contents) */}
      <div className="vorbe-details" id={compact ? 'vocal2-details' : undefined} hidden={compact && !detailsOpen}>

        {/* Ligne prompt — echo du dernier message système */}
        <p className="vorbe-prompt">
          {!armed
            ? 'Touchez le cercle pour activer la saisie vocale.'
            : prompt || VOICE_LABEL[recorderState]}
        </p>

        {/* Entrée texte (US-VMPL-002) : pilotage SON de /vocal2 et repli clavier ; même routage que la voix */}
        {compact && (
          <form
            data-son="vocal2/dire"
            data-phase={phase}
            data-prompt={prompt}
            data-patient={ctx.patient?.patient_id ?? ''}
            className="d-flex gap-2 w-100"
            onSubmit={(e) => {
              e.preventDefault();
              entendu(texte);
              setTexte('');
            }}
          >
            <input
              className="form-control form-control-sm"
              aria-label="Écrire au lieu de parler"
              placeholder="Écrire au lieu de parler"
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
            />
            <button type="submit" className="btn btn-sm btn-primary">Dire</button>
          </form>
        )}

        {!compact && armed && <span className="vorbe-hint">{VOICE_LABEL[recorderState]}</span>}

        {/* Panneaux contextuels — miroir vivant du dialogue (A2, plan §2) */}
        {armed && <ContextPanel phase={phase} ctx={ctx} />}

        {/* Diagnostic TTS — jamais muet silencieux (C, plan §4.5) */}
        {ttsStatus === 'unavailable' && (
          <span className="vorbe-tts-warn" role="status">
            <i className="bi bi-volume-mute me-1" />voix indisponible
          </span>
        )}

        {error && <span className="vorbe-error"><i className="bi bi-exclamation-triangle me-1" />{error}</span>}
      </div>

      {/* Journal des saisies (live) — réutilise JournalPanel + useDpiPolling (A3, DRY) */}
      {!compact && dpi && (
        <div className="vorbe-journal">
          <JournalPanel journal={dpi.journal} />
        </div>
      )}
    </div>
  );
}
